/**
 * Unified Services Module
 * Central export point for all services and service infrastructure
 */

// Export core service infrastructure
export * from "./core";

// Initialize legacy interceptors
import "./legacy/api/interceptors";

// Export primary services (formerly v2)
export { authService } from "./auth.service";
export { currencyService } from "./currency.service";
export { leadService } from "./lead.service";
export { propertyService } from "./property.service";
export { searchService } from "./search.service";
export { userService } from "./user.service";

// Export unified service implementations
export * from "./unified-auth.service";
export * from "./unified-property.service";

// Legacy service compatibility exports (removed - old files no longer exist)
// export { default as legacyAuthService } from "./auth.service.old";
// export { propertyService as legacyPropertyService } from "./property.service.old";

// Import primary services
import { authService } from "./auth.service";
import { serviceFactory } from "./core/service-factory";
import { currencyService } from "./currency.service";
import { leadService } from "./lead.service";
import { propertyService } from "./property.service";
import { searchService } from "./search.service";

// Convenience imports for unified services
import { unifiedAuthService } from "./unified-auth.service";
import { unifiedPropertyService } from "./unified-property.service";
import { userService } from "./user.service";

// Default service instances (primary services)
export const services = {
  auth: authService,
  property: propertyService,
  user: userService,
  currency: currencyService,
  search: searchService,
  lead: leadService,
  // Unified services for advanced use cases
  unifiedAuth: unifiedAuthService,
  unifiedProperty: unifiedPropertyService,
  factory: serviceFactory,
} as const;

// Service management utilities
export const initializeServices = async (): Promise<void> => {
  // Services are automatically initialized when imported
  // This function is for any additional setup if needed
};

export const getServiceHealth = async (): Promise<Record<string, any>> => {
  return serviceFactory.healthCheckAll();
};

export const getServiceMetrics = async (): Promise<Record<string, any>> => {
  return serviceFactory.getAllMetrics();
};

// Type-safe service getter
export const getService = <T extends keyof typeof services>(
  name: T,
): (typeof services)[T] => {
  return services[name];
};

// Enhanced error types for consumers
export type {
  QueryParams,
  RequestContext,
  ServiceConfig,
  ServiceError,
  ServiceErrorCode,
  ServiceMetrics,
  ServiceResponse,
  ValidationResult,
  ValidationSchema,
} from "./core/types";

export type {
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  NotFoundError,
  RateLimitError,
  ServerError,
  TimeoutError,
  UnifiedServiceError,
  ValidationError,
} from "./core/errors";

// Service-specific types
export type {
  AuthResponse,
  ForgotPasswordRequest,
  LoginCredentials,
  RegisterData,
  ResetPasswordRequest,
} from "./unified-auth.service";

export type {
  PropertyListResponse,
  PropertyResponse,
  PropertySearchParams,
} from "./unified-property.service";

// Backward compatibility - map service names for clarity
// Note: These are already exported directly above, this is just for clarity
export { authService as defaultAuthService } from "./auth.service";
export { propertyService as defaultPropertyService } from "./property.service";
export { userService as defaultUserService } from "./user.service";

export default services;
